<template>
  <div :class="rootStyles">
    <JerseyNumbers v-if="isJerseyNumbers" class="product-details-dtf__jersey-numbers" />
    <Letters v-if="isLetters" class="product-details-dtf__letters" />

    <div
      v-if="firstArtwork && !firstArtwork.isSimulated && !showStepsUI"
      class="product-details-dtf__file-name"
      :class="{
        'product-details-dtf__file-name--with-error': isGenericErrorVisible,
        'product-details-dtf__file-name--full-width': showFullWidthArtwork
      }"
    >
      <UseSvg
        v-if="!!firstArtwork.metadata || isGenericErrorVisible"
        :icon="headerIcon"
        class="product-details-dtf__header-icon"
        :class="{ 'product-details-dtf__header-icon--with-error': isGenericErrorVisible }"
      />
      <span class="product-details-dtf__header-text"> {{ firstArtwork.name }} </span>
    </div>

    <div
      v-if="!outOfStock && !isMobile && ($slots.dates || (gangSheetPdp && $slots['free-shipping']))"
      class="product-details-dtf__dates-container hidden-xs"
    >
      <slot v-if="gangSheetPdp" name="free-shipping" />
      <TransferShippingBox
        v-if="showTransferShippingBox && !isMobile"
        :artwork="firstArtwork"
        :show-pro-color-upsell="showProColorUpsell"
        :is-mobile="false"
      />
      <DeliveryDate v-else />
    </div>

    <template v-if="isGangSheetBuilder">
      <GangSheetBuilderPdpContainer :current-store="currentStore" />
      <slot name="saved-carts"></slot>
    </template>
    <template v-if="!isJerseyNumbers && !isLetters && !isGangSheetBuilder">
      <ProColorUpsell
        v-if="isStandardArtwork && showProColorUpsell && !showTransferShippingBox"
        :artwork="firstArtwork"
        class="product-details-dtf__pro-color-upsell"
      />

      <!-- Mobile TransferShippingBox - positioned above Upload Your Design section ONLY when experiment is ON -->
      <div
        v-if="!outOfStock && showTransferShippingBox && isMobile"
        class="product-details-dtf__dates-container product-details-dtf__dates-container--mobile-above-upload visible-xs"
      >
        <slot v-if="gangSheetPdp" name="free-shipping" />
        <TransferShippingBox
          :artwork="firstArtwork"
          :show-pro-color-upsell="showProColorUpsell"
          :is-mobile="true"
        />
      </div>
      <DesignTagCreationPanel v-if="!outOfStock && neckLabel" />
      <StepWrapper
        v-if="!outOfStock && !neckLabel"
        :has-error="showStepsUI && isGenericErrorVisible"
        :borderless="!showStepsUI"
        :step-number="1"
        header-text="Upload Your Design(s)"
      >
        <template #header-badge>
          <FreeTransfersBadge
            v-if="showFreeTransfersBadge"
            :message="freeTransfersBadgeMessage"
            class="product-details-dtf__free-transfers-badge"
          />
        </template>
        <template v-if="showCancelButton" #step-number-content>
          <button
            type="button"
            class="product-details-dtf__cancel"
            @click="handleCancelProcess(firstArtwork)"
          >
            <UseSvg class="product-details-dtf__cancel-icon" icon="modal-close" />
            Cancel
          </button>
        </template>
        <ArtworkInfo :artwork="firstArtwork" :show-file-info="showArtworkFileInfo" :index="0" />
        <ArtworkImage
          :key="firstArtwork.id"
          :artwork="firstArtwork"
          :active-config-section="activeConfigSection"
          show-full-screen-button
          @click="showFileDialog"
        />
        <TrimInfo
          v-if="!isStandardArtwork && !firstArtwork.isFile && !firstArtwork.isSimulated"
          :id="firstArtwork.id"
          :artwork="firstArtwork"
          :background="firstArtwork.background"
          :can-remove-bg="firstArtwork.canRemoveBg"
          :remove-background="firstArtwork.removeBackground"
          :toggle-background-error="firstArtwork.toggleBackgroundError"
        />
        <ArtworkQualityConfigPanel
          v-if="shouldShowArtworkQualityConfigPanel"
          :key="firstArtwork.id"
          :artwork="firstArtwork"
          :active-config-section="activeConfigSection"
          @set-active-config-section="setActiveConfigSection"
        />
        <ArtworkQualityConfigPanelAbAiMonetization
          v-if="shouldShowArtworkQualityConfigPanelAbAiMonetization"
          :index="0"
          :artwork="firstArtwork"
          :reorder-enabled="reorderEnabled"
          :active-config-section="activeConfigSection"
          :paid-expert-edit-addon-enabled="paidExpertEditAddonEnabled"
          @set-active-config-section="setActiveConfigSection"
        />
        <PuffColorSelector v-if="isPuff && !showStepsUI" label="Select Puff Color" />
        <template v-if="sticker && artworkUploaded && experiments.stickers_style_options">
          <StickersTools :artwork="firstArtwork" class="product-details-dtf__section" />
        </template>
        <template v-if="!isStandardArtwork">
          <RedrawNotes v-if="showRedrawNote" :enhancement-note="firstArtwork.enhancementNote" />
          <ProColorUpsell
            v-if="showProColorUpsell && !showTransferShippingBox"
            :artwork="firstArtwork"
            class="product-details-dtf__section"
          />
        </template>
        <ArtworkCTA v-if="showArtworkCTA" :artwork-url="firstArtwork.url" />
        <ArtworkError
          v-if="withError && !isMultiSizeError"
          :artwork="firstArtwork"
          :current-store="currentStore"
        />
        <ArtworkNotification v-if="!withError && warningMessage">
          <b>{{ warningMessage }}</b>
        </ArtworkNotification>
      </StepWrapper>
      <PuffColorSelector v-if="isPuff && showStepsUI" label="Select Puff Color" />
      <template v-if="!showModal && !neckLabel">
        <MultiSizeStep
          v-if="showMultiSize"
          :artwork="firstArtwork"
          :disabled="disableSizeChange"
          :readonly="printAsIsReadOnly"
          :with-pre-cut="withPreCut"
          :show-multi-upload-box="showMultiUploadBox"
          :is-artwork-uploaded="artworkUploaded"
          :show-free-transfers-badge="showFreeTransfersBadge"
          @show-file-dialog="showFileDialog"
        />
        <ArtworkDimensions
          v-else-if="!isStickerSample && !showMultiSize"
          :artwork="firstArtwork"
          :class="{
            'product-details-dtf__dimensions--near-quantity':
              !showStepsUI && printAsIs && modalArtworks.length <= 1
          }"
          :disabled="disableSizeChange"
          :width-default="defaultWidthValue"
          :readonly="printAsIsReadOnly"
          :current-store="currentStore"
        />
      </template>
      <DtfSpanglesActions v-if="showSpanglesActions" :is-artwork-invalid="withError" />
      <DtfSpangleVersions v-if="showSpangleVersions && !isSpangleReordering" />
      <DtfSpanglesColorSelector v-if="showSpangleVersions || isSpangleReordering" />
    </template>
    <template v-if="isStickerSample">
      <input
        class="artwork-quantity__input js-quantity-form-input"
        type="hidden"
        :data-variant-id="variantId"
        :value="10"
      />
    </template>
    <ArtworkQuantity
      v-else-if="!showMultiSize && !isGangSheetBuilder && !neckLabel"
      :artwork="firstArtwork"
      show-free-shipping
      :disabled="outOfStock"
    />
    <template v-if="!neckLabel">
      <PreCut v-if="withPreCut && !showMultiSize && !neckLabel" />
      <NotifyOnRestock v-if="outOfStock" />
      <DropZone v-if="!outOfStock && !neckLabel" />
    </template>

    <input
      id="product-details-dtf-file-input"
      ref="file"
      type="file"
      data-testid="product-details-dtf-file-input"
      :accept="supportedFileTypes"
      class="product-details-dtf__file hidden"
      :multiple="(!isSpangle || spanglesMaxImageCount > 1) && !isPuff && !isStickerSample"
      @change="handleFileChange"
    />

    <MultiArtworkModal
      v-if="showModal"
      :key="firstArtwork.id"
      :move-bellow="!!fileToRemove || showDiscardModal || showFilesError"
      :current-store="currentStore"
      :active-config-section="activeConfigSection"
      @upload="showFileDialog"
    >
      <template #saved-carts>
        <slot name="saved-carts-modal"></slot>
      </template>
      <template #free-shipping>
        <slot name="free-shipping"></slot>
      </template>
    </MultiArtworkModal>

    <ArtworkHistoryModal v-if="showArtworkHistoryModal" :reorder-enabled="reorderEnabled" />

    <!-- Mobile DeliveryDate/ProColor - original position when TransferShippingBox experiment is OFF -->
    <div
      v-if="!outOfStock && !showTransferShippingBox"
      class="product-details-dtf__dates-container visible-xs"
    >
      <slot v-if="gangSheetPdp" name="free-shipping" />
      <DeliveryDate />
    </div>

    <StickerSampleBanner v-if="isStickerSample" />

    <div v-if="showMultiUploadBox && !showMultiSize" class="product-details-dtf__upload">
      <UploadBox @upload="showFileDialog" />
    </div>

    <template v-if="!isGangSheetBuilder">
      <StepWrapper v-if="showMultiSize" :step-number="3" header-text="Review and Add to Cart">
        <MultiSizeTotal />
        <div class="js-product-details-delivery-date-pdp-container hidden">
          <DeliveryDate hide-address-line compact is-same-day />
          <span class="product-details-dtf__or">Or</span>
        </div>
        <slot v-if="showShippingProgress" name="free-shipping"></slot>
        <div v-if="showStoreCredit" class="product-details-dtf__store-credit">
          <TransfersStoreCredit :store-credit-info="storeCreditInfo" />
        </div>
        <slot name="saved-carts"></slot>
        <DtfAddToCart v-if="!showModal" />
        <slot name="favorite"></slot>
      </StepWrapper>
      <template v-else>
        <div>
          <TransfersStoreCredit v-if="showStoreCredit" :store-credit-info="storeCreditInfo" />
          <slot name="saved-carts"></slot>
          <DtfAddToCart v-if="!showModal && !neckLabel" />
        </div>
      </template>
    </template>

    <DtfConfirmationModal v-if="fileToRemove" @close="resetRemovalFile" @submit="onRemoveArtwork">
      <template #header>Remove File</template>
      <template #text>This file will be removed. Do you confirm?</template>
      <template #button>Remove File</template>
    </DtfConfirmationModal>

    <DtfConfirmationModal v-if="showDiscardModal" @close="hideDiscardModal" @submit="onDiscard">
      <template #header>Discard Changes</template>
      <template #text>
        All the changes and artworks will be discarded.<br />Are you sure you want to proceed?
      </template>
      <template #button>Discard Changes</template>
    </DtfConfirmationModal>

    <DtfConfirmationModal v-if="showFilesError" @close="resetFileErrors">
      <template #header>Warning</template>
      <template #text> You can only upload up to 10 files simultaneously. </template>
      <template #cancel>OK</template>
    </DtfConfirmationModal>

    <GangSheetInfoModal v-if="showGangSheetInfoModal" :current-store="currentStore" />
  </div>
</template>

<script>
// Global imports
import { mapActions, mapState } from "pinia";
import { toRef } from "vue";
import { useI18n } from "vue-i18n";

// Absolute paths
import { acceptedFileTypes } from "common/file-type-helper";
import { readCookie } from "common/utils/cookie";
import { api } from "jiffy-api";
import { EventBus, Events } from "jiffy-store";
import initDeliveryDateSimple from "components/jiffy/product-details-delivery-date-simple/product-details-delivery-date-simple";
import initProductSavedCartsSelector from "components/jiffy/product-saved-carts-selector/product-saved-carts-selector";
import JerseyNumbers from "components/jiffy/vue-product-details-dtf/JerseyNumbers/JerseyNumbers.vue";
import Letters from "components/jiffy/vue-product-details-dtf/Letters/Letters.vue";
import ProColorUpsell from "components/jiffy/vue-product-details-dtf/ProColorUpsell.vue";
import PuffColorSelector from "components/jiffy/vue-product-details-dtf/PuffColorSelector.vue";
import StickersTools from "components/jiffy/vue-product-details-dtf/Stickers/StickersTools/StickersTools.vue";
import {
  isArtworkWithError,
  toggleGlobalDtfError
} from "components/jiffy/vue-product-details-dtf/utils";
import { PDP_TYPE_GANG_SHEETS, DTF001_CATALOG_NUMBER } from "./common/constants";

// Relative paths
import UseSvg from "../vue-cart/UseSvg.vue";
import ArtworkCTA from "./ArtworkCTA.vue";
import ArtworkDimensions from "./ArtworkDimensions.vue";
import ArtworkError from "./ArtworkError.vue";
import ArtworkHistoryModal from "./ArtworkHistoryModal.vue";
import ArtworkImage from "./ArtworkImage.vue";
import ArtworkInfo from "./ArtworkInfo.vue";
import MultiSizeStep from "./ArtworkMultiSize/MultiSizeStep.vue";
import MultiSizeTotal from "./ArtworkMultiSize/MultiSizeTotal.vue";
import ArtworkNotification from "./ArtworkNotification.vue";
import ArtworkQualityConfigPanel from "./ArtworkQualityConfigPanel.vue";
import ArtworkQualityConfigPanelAbAiMonetization from "./ArtworkQualityConfigPanel/ArtworkQualityConfigPanel.vue";
import ArtworkQuantity from "./ArtworkQuantity.vue";
import StepWrapper from "./common/StepWrapper.vue";
import FreeTransfersBadge from "./common/FreeTransfersBadge.vue";
import TransfersStoreCredit from "./common/TransfersStoreCredit.vue";
import DeliveryDate from "./DeliveryDate.vue";
import DesignTagCreationPanel from "./DesignTagCreationPanel.vue";
import DropZone from "./DropZone.vue";
import DtfAddToCart from "./DtfAddToCart.vue";
import DtfConfirmationModal from "./DtfConfirmationModal.vue";
import DtfSpanglesActions from "./DtfSpanglesActions.vue";
import DtfSpanglesColorSelector from "./DtfSpanglesColorSelector.vue";
import DtfSpangleVersions from "./DtfSpangleVersions.vue";
import GangSheetBuilderPdpContainer from "./GangSheetBuilderPdp/GangSheetBuilderPdpContainer.vue";
import GangSheetInfoModal from "./GangSheetInfoModal.vue";
import MultiArtworkModal from "./MultiArtworkModal.vue";
import NotifyOnRestock from "./NotifyOnRestock.vue";
import PreCut from "./PreCut.vue";
import RedrawNotes from "./RedrawNotes.vue";
import { SPANGLE_MODES, useSpanglesStore } from "./spangles-module";
import StickerSampleBanner from "./StickerSampleBanner.vue";
import { useDtfStore } from "./store";
import TransferShippingBox from "./TransferShippingBox.vue";
import TrimInfo from "./TrimInfo.vue";
import UploadBox from "./UploadBox.vue";

export default {
  name: "ProductDetailsDtf",
  components: {
    JerseyNumbers,
    UploadBox,
    ArtworkCTA,
    ArtworkImage,
    ArtworkDimensions,
    ArtworkQuantity,
    ArtworkQualityConfigPanel,
    ArtworkQualityConfigPanelAbAiMonetization,
    MultiArtworkModal,
    DtfAddToCart,
    ArtworkInfo,
    DtfConfirmationModal,
    TrimInfo,
    GangSheetInfoModal,
    PreCut,
    StickerSampleBanner,
    ArtworkError,
    ArtworkNotification,
    UseSvg,
    ArtworkHistoryModal,
    NotifyOnRestock,
    DropZone,
    DtfSpanglesActions,
    DtfSpangleVersions,
    DtfSpanglesColorSelector,
    PuffColorSelector,
    ProColorUpsell,
    Letters,
    StickersTools,
    RedrawNotes,
    TransferShippingBox,
    MultiSizeStep,
    StepWrapper,
    FreeTransfersBadge,
    MultiSizeTotal,
    TransfersStoreCredit,
    GangSheetBuilderPdpContainer,
    DeliveryDate,
    DesignTagCreationPanel
  },
  props: {
    locale: {
      type: String,
      default: "en-US"
    },
    currentStore: {
      type: String,
      default: null
    }
  },
  setup(props) {
    const { locale, t } = useI18n({ useScope: "global" });
    const pdpLocale = toRef(props, "locale");
    const pdpStore = useDtfStore();

    pdpStore.setLocale(pdpLocale.value);
    locale.value = pdpLocale.value;
    return { t };
  },
  data() {
    return {
      sizeMessageMaxSize: 10,
      showComparisonModal: false,
      activeConfigSection: "jiffy-print-perfect", // jiffy-print-perfect | print-as-uploaded
      hasNoPrintTransfersHistory: null,
      cartHasBlanks: false
    };
  },
  computed: {
    ...mapState(useDtfStore, [
      "artworks",
      "files",
      "fileToRemove",
      "showDiscardModal",
      "showFilesError",
      "replaceArtwork",
      "modalArtworks",
      "outOfStock",
      "pdpType",
      "printAsIs",
      "showCTA",
      "showModal",
      "sticker",
      "withPreCut",
      "showArtworkHistoryModal",
      "isMobile",
      "catalogNumber",
      "isSticker",
      "isStickerSample",
      "isSpangle",
      "isPuff",
      "isLetters",
      "isFirstArtworkMinQuantityInvalid",
      "variantId",
      "experiments",
      "isJerseyNumbers",
      "isGuest",
      "showStepsUI",
      "showRedrawUI",
      "showMultiSize",
      "isStandardArtwork",
      "enableAbAiMonetization",
      "simulation",
      "storeCreditInfo",
      "isGangSheetBuilder",
      "showModal",
      "reorderEnabled",
      "paidExpertEditAddonEnabled",
      "neckLabel",
      "blankItemsCount",
      "transfersInCartCount",
      "isBogoApplicable",
      "isNewToTransfers"
    ]),
    ...mapState(useSpanglesStore, [
      "selectedAlgorithm",
      "spangleVariations",
      "spangleMode",
      "lastSpangledDimensions",
      "spangleSizeEdited",
      "spanglesLoading",
      "spanglesMaxImageCount",
      "invalidSpangleSelected",
      "allAPIsCompleted",
      "allAPIsFailed",
      "isSpangleReordering",
      "originalArtworkUrl"
    ]),
    shouldShowArtworkQualityConfigPanel() {
      if (!this.hasArtwork) {
        return false;
      }

      return (
        this.isStandardArtwork &&
        !this.printAsIs &&
        !this.firstArtwork.isFile &&
        !this.firstArtwork.isSimulated &&
        !this.enableAbAiMonetization
      );
    },
    shouldShowArtworkQualityConfigPanelAbAiMonetization() {
      if ((this.withError && !this.isMultiSizeError) || !this.hasArtwork) {
        return false;
      }

      return (
        this.isStandardArtwork &&
        !this.printAsIs &&
        !this.firstArtwork.isFile &&
        !this.firstArtwork.isSimulated &&
        this.enableAbAiMonetization
      );
    },
    displayEnhancementFeedback() {
      return (
        this.enableAbAiMonetization &&
        this.isStandardArtwork &&
        !this.firstArtwork.isSimulated &&
        this.firstArtwork.id &&
        this.firstArtwork.enhancementCompleted &&
        this.firstArtwork.enhancementTier === "full_enhancements"
      );
    },
    sampleItemPrice() {
      return Math.round(this.simulation.pricePerInch * 100) / 100;
    },
    firstArtwork() {
      return this.artworks[0];
    },
    isArtworkLoading() {
      return this.firstArtwork?.isLoading;
    },
    artworkUploaded() {
      const artworkId = parseInt(this.firstArtwork?.id, 10);
      if (Number.isNaN(artworkId)) {
        return false;
      }

      return typeof artworkId === "number" && !this.isArtworkLoading;
    },
    warningMessage() {
      return this.firstArtwork?.metadata?.warnings?.human;
    },
    defaultWidthValue() {
      if (!this.printAsIs) return "";

      return "22";
    },
    printAsIsReadOnly() {
      return this.printAsIs && !!this.firstArtwork?.width && !!this.firstArtwork?.height;
    },
    showSpanglesActions() {
      return this.isSpangle && this.artworkUploaded && !this.isArtworkLoading;
    },
    showSpangleVersions() {
      return this.showSpanglesActions && this.allAPIsCompleted;
    },
    showMultiUploadBox() {
      return (
        !this.files.length &&
        !this.firstArtwork.isSimulated &&
        !this.isSpangle &&
        !this.isPuff &&
        !this.isStickerSample
      );
    },
    showGangSheetInfoModal() {
      return (
        !this.isGuest && this.gangSheetPdp && readCookie("gang-sheet-info-modal-shown") !== "true"
      );
    },
    gangSheetPdp() {
      return this.pdpType === PDP_TYPE_GANG_SHEETS;
    },
    supportedFileTypes() {
      return acceptedFileTypes(this.printAsIs);
    },
    showArtworkCTA() {
      return this.showCTA && this.firstArtwork?.url;
    },
    isMultiSizeError() {
      return this.firstArtwork?.isMultiSizeError;
    },
    hasArtwork() {
      return this.firstArtwork && !this.firstArtwork.isSimulated && !this.firstArtwork.uploadError;
    },
    withError() {
      return isArtworkWithError(this.firstArtwork);
    },
    isGenericErrorVisible() {
      if (!this.firstArtwork || this.firstArtwork.isSimulated) {
        return false;
      }
      return this.withError || this.isFirstArtworkMinQuantityInvalid;
    },
    headerIcon() {
      return this.isGenericErrorVisible ? "strong-info" : "check-circle-bold";
    },
    fileSize() {
      return Math.floor((this.firstArtwork?.file?.size || 0) * 0.000001 || 0);
    },
    remainingTime() {
      const spangleTime = this.isSpangle ? 1 : 0;

      if (Math.ceil(this.fileSize / 10) < 1) {
        return 1 + spangleTime;
      }
      return Math.min(Math.ceil(this.fileSize / 10), this.sizeMessageMaxSize) + spangleTime;
    },
    disableSizeChange() {
      if (this.isStandardArtwork) {
        return this.outOfStock || this.isArtworkLoading || !this.firstArtwork.enhancementCompleted;
      }
      if (this.isSpangle) {
        if (this.allAPIsFailed) {
          return this.isArtworkLoading;
        }
        return (
          this.outOfStock ||
          this.spangleSizeEdited ||
          this.spanglesLoading ||
          this.isArtworkLoading ||
          this.invalidSpangleSelected ||
          !this.allAPIsCompleted
        );
      }
      return this.outOfStock || this.isArtworkLoading;
    },
    showArtworkFileInfo() {
      return !this.isSpangle;
    },
    showProColorUpsell() {
      return (
        this.catalogNumber === DTF001_CATALOG_NUMBER && this.currentStore === "usa" && !this.isGuest
      );
    },
    showTransferShippingBox() {
      // Check if experiment is active (variant > 0)
      return this.experiments?.ab_transfer_shipping_box_value_props > 0;
    },

    rootStyles() {
      return {
        "product-details-dtf": true,
        "product-details-dtf--jersey-numbers": this.isJerseyNumbers
      };
    },
    showFullWidthArtwork() {
      return !this.sticker;
    },
    showRedrawNote() {
      if (this.pdpType === PDP_TYPE_GANG_SHEETS) return false;
      return this.showRedrawUI;
    },
    showShippingProgress() {
      return this.firstArtwork && !this.firstArtwork.isSimulated && !this.isArtworkLoading;
    },
    showStoreCredit() {
      return (
        this.artworkUploaded &&
        !this.isSticker &&
        !this.isSpangle &&
        !this.isGuest &&
        Number(this.storeCreditInfo.orderSubtotal) > 0
      );
    },
    artworkEnhancementProcessing() {
      return (
        this.isStandardArtwork && !this.isArtworkLoading && !this.firstArtwork.enhancementCompleted
      );
    },
    showCancelButton() {
      const isEnhancing = this.artworkUploaded && this.artworkEnhancementProcessing;
      return !this.withError && !this.isSpangle && (this.isArtworkLoading || isEnhancing);
    },
    showFreeTransfersBadge() {
      if (!this.isBogoApplicable) {
        return false;
      }

      const isEnhancing = this.artworkUploaded && this.artworkEnhancementProcessing;
      const previewReady = !this.isArtworkLoading && !isEnhancing;

      if (!previewReady || !this.artworkUploaded) {
        return false;
      }

      if (this.cartHasBlanks !== true && this.cartHasBlanks !== "true") {
        return false;
      }

      if (this.isGuest) {
        return true;
      }

      if (this.isNewToTransfers === null || this.isNewToTransfers === undefined) {
        return false;
      }

      return this.isNewToTransfers === true;
    },
    freeTransfersBadgeMessage() {
      return `You have ${this.blankItemsCount} FREE Transfers!`;
    }
  },
  watch: {
    isGenericErrorVisible(value) {
      if (this.showStepsUI) {
        return;
      }
      toggleGlobalDtfError(value);
    },
    artworkUploaded(newValue) {
      if (this.isStandardArtwork) {
        this.activeConfigSection = this.firstArtwork?.enhanceImage
          ? "jiffy-print-perfect"
          : "print-as-uploaded";

        if (newValue) {
          const newUrl = new URL(window.location);
          newUrl.searchParams.delete("artwork_urls");
          newUrl.searchParams.delete("file_content_type");
          window.history.replaceState({}, "", newUrl.toString());
        }
      }

      if (this.isSpangle && newValue) {
        const url = this.isSpangleReordering ? this.originalArtworkUrl : this.firstArtwork?.url;

        this.setOriginalArtworkUrl(url);
      }

      if (newValue) {
        this.checkCartForBlanks();
      }
    },
    spangleMode(newValue) {
      if (newValue === SPANGLE_MODES.sizeEdited) {
        this.updateArtworkValues({ index: 0, ...this.lastSpangledDimensions });
      }
    },
    spangleVariations(newVariations) {
      const version = newVariations.find(({ algorithm }) => algorithm === this.selectedAlgorithm);
      this.selectVersion({ version });
    }
  },
  mounted() {
    initProductSavedCartsSelector(true);
    initDeliveryDateSimple();
    this.initDefaultValues();

    window.JiffyDeliverTo.reloadToggle();

    document
      .querySelector(".js-product-details-dtf-sheet")
      ?.classList.remove("product-details-dtf-sheet--mounting");

    this.checkFreeTransfersBadgeConditions();
    this.setupCartListeners();
  },
  beforeUnmount() {
    const store = useDtfStore();
    store.cleanupPolling();
    this.cleanupCartListeners();
  },
  methods: {
    ...mapActions(useDtfStore, [
      "addFilesToQueue",
      "getArtworksPricing",
      "updatePdpDates",
      "removeArtworkWithActivity",
      "initDefaultValues",
      "addSampleToCart",
      "updateEnableEnhancement",
      "updateArtworkValuesById",
      "setActiveConfigSection"
    ]),
    ...mapActions(useSpanglesStore, ["selectVersion"]),
    ...mapActions(useDtfStore, [
      "resetRemovalFile",
      "removeArtwork",
      "removeArtworkByIndex",
      "resetFilesError",
      "setReplaceArtwork",
      "hideDiscardModal",
      "dropMultiArtwork",
      "updateArtworkValues",
      "resetPuff",
      "setArtworkBackground",
      "handleCancelProcess",
      "setBlankItemsCount",
      "setTransfersInCartCount",
      "setIsNewToTransfers"
    ]),
    ...mapActions(useSpanglesStore, ["resetSpangles", "setOriginalArtworkUrl"]),
    showFileDialog(data) {
      // Source of the event will determine if we are replacing
      // an artwork
      this.setReplaceArtwork(Boolean(data?.replaceArtwork));
      this.$refs.file.click();
    },
    handleFileChange(event) {
      this.resetSpangles();
      this.resetPuff();
      const { files } = event.target;
      let artworkIndex = null;

      if (!files.length) return;
      if (files.length > 10) {
        this.showFilesError = true;
        return;
      }

      if (files.length <= 1 && this.replaceArtwork) {
        artworkIndex = 0;
      } else {
        this.artworks.forEach((artwork, index) => {
          if (isArtworkWithError(artwork)) {
            this.removeArtworkByIndex(index);
          }
        });
      }

      const uploadSource = event.target.getAttribute("data-upload-source");
      this.addFilesToQueue({ files, isDropEvent: false, artworkIndex, uploadSource });
      this.$refs.file.value = "";
      this.setReplaceArtwork(false);
    },
    changeArtworkBgColor(color) {
      this.setArtworkBackground(color);
    },
    handleDesignServiceClick() {
      if (this.shouldShowArtworkQualityConfigPanelAbAiMonetization) {
        this.activateDesignServiceForMonetizationV2();
      } else {
        this.setActiveConfigSection("jiffy-print-perfect");
      }
      this.focusDesignInstructionsTextarea();
    },
    activateDesignServiceForMonetizationV2() {
      // Click on the PrintPerfect tab if not already selected
      if (this.firstArtwork.enhancementTier !== "full_enhancements") {
        this.updateArtworkValuesById({
          id: this.firstArtwork.id,
          enhancementTier: "full_enhancements",
          pricing: this.cachedArtworkPrices?.full_enhancements || {},
          hasExpertEditAddon: true
        });
        this.updateEnableEnhancement({
          artworkId: this.firstArtwork.id,
          enableEnhancement: true,
          enhancementTier: "full_enhancements"
        });
      }
      // Check the expert edit addon checkbox if not already checked
      if (!this.firstArtwork.hasExpertEditAddon) {
        this.updateArtworkValuesById({
          id: this.firstArtwork.id,
          hasExpertEditAddon: true,
          enhancementNote: ""
        });
        this.getArtworksPricing();
      }
    },
    focusDesignInstructionsTextarea() {
      this.$nextTick(() => {
        const $customNotesTextarea = document.querySelector(".custom-notes__textarea");
        // RedrawNotes component uses dynamic IDs with index (defaults to 0)
        // Try the default ID first, then fall back to any redraw-note-* ID
        const $redrawTextarea =
          document.getElementById("redraw-note-0") ||
          document.querySelector('[id^="redraw-note-"]');
        const $textarea = $customNotesTextarea || $redrawTextarea;
        if ($textarea) {
          $textarea.focus();
          $textarea.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    onRemoveArtwork() {
      this.removeArtworkWithActivity(this.fileToRemove);
      this.getArtworksPricing();
      this.updatePdpDates();
      this.resetSpangles();
      this.resetPuff();
    },
    onDiscard() {
      this.hideDiscardModal();
      this.dropMultiArtwork();
      this.updatePdpDates();
      this.resetSpangles();
      this.resetPuff();
    },
    toggleComparisonModal() {
      this.showComparisonModal = !this.showComparisonModal;
    },
    setActiveConfigSection(section) {
      this.activeConfigSection = section;
    },
    async checkFreeTransfersBadgeConditions() {
      try {
        if (this.isGuest) {
          this.setIsNewToTransfers(true);
        } else {
          try {
            const response = await api.dtf.isNewToDtfForBogo();

            const isNew =
              typeof response === "string"
                ? response.trim().toLowerCase() === "true"
                : Boolean(response);

            this.setIsNewToTransfers(isNew);
          } catch (error) {
            console.warn("Failed to check if user is new to transfers:", error);
            this.setIsNewToTransfers(false);
          }
        }

        this.hasNoPrintTransfersHistory = this.isNewToTransfers;

        // Check if cart has at least one Blank item
        await this.checkCartForBlanks();
      } catch (error) {
        console.warn("Failed to check free transfers badge conditions:", error);
      }
    },
    async checkCartForBlanks() {
      try {
        const response = await api.vueCart.currentItems();
        if (Object.keys(response.items || {}).length === 0) {
          this.updateCartCounts(0, 0);
          return;
        }

        const cartResponse = await api.vueCart.getCartState({ cb: Date.now() });
        if (!cartResponse?.order_groups) {
          this.updateCartCounts(0, 0);
          return;
        }

        const groups = cartResponse.order_groups.groups || {};
        const { blanks, transfers } = this.countItemsInGroups(groups);

        this.updateCartCounts(blanks, transfers);
      } catch (error) {
        console.warn("Failed to check cart for blanks:", error);
        this.updateCartCounts(0, 0);
      }
    },
    updateCartCounts(blanksCount, transfersCount) {
      this.setBlankItemsCount(blanksCount);
      this.setTransfersInCartCount(transfersCount);
      this.cartHasBlanks = blanksCount > 0;
    },
    countItemsInGroups(groups) {
      return Object.values(groups).reduce(
        (totals, group) => {
          const lineItems = group?.line_items || group?.lineItems || {};
          const counts = this.countItemsInLineItems(lineItems);
          return {
            blanks: totals.blanks + counts.blanks,
            transfers: totals.transfers + counts.transfers
          };
        },
        { blanks: 0, transfers: 0 }
      );
    },
    countItemsInLineItems(lineItems) {
      return Object.values(lineItems).reduce(
        (totals, lineItem) => {
          if (!lineItem) return totals;
          const quantity = parseInt(lineItem.quantity, 10) || 0;
          const itemType = this.getLineItemType(lineItem);
          if (itemType === "blanks") {
            return { ...totals, blanks: totals.blanks + quantity };
          }
          if (itemType === "transfers") {
            // Only count DTF001 transfers for BOGO promotion calculation
            const productStyle = lineItem.productStyle || lineItem.product_style;
            const catalogNumber =
              lineItem.product?.catalog_number ||
              lineItem.catalog_number ||
              lineItem.product?.product_style ||
              productStyle;
            const isDtf001 =
              catalogNumber === DTF001_CATALOG_NUMBER || productStyle === DTF001_CATALOG_NUMBER;
            if (isDtf001) {
              return { ...totals, transfers: totals.transfers + quantity };
            }
          }
          return totals;
        },
        { blanks: 0, transfers: 0 }
      );
    },
    getLineItemType(lineItem) {
      const businessLine = String(lineItem.business_line || "").toLowerCase();
      const productBusinessLine = String(lineItem.product?.business_line || "").toLowerCase();
      if (businessLine === "blanks" || productBusinessLine === "blanks") return "blanks";
      if (businessLine === "transfers" || productBusinessLine === "transfers") return "transfers";
      return null;
    },
    setupCartListeners() {
      // Listen for cart updates to re-check for Blank items
      this.handleItemsAddedToCart = () => {
        this.checkCartForBlanks();
      };
      this.handleCartQuantityUpdated = () => {
        this.checkCartForBlanks();
      };

      EventBus.on(Events.ITEMS_ADDED_TO_CART, this.handleItemsAddedToCart);
      EventBus.on(Events.CART_QUANTITY_UPDATED, this.handleCartQuantityUpdated);
    },
    cleanupCartListeners() {
      if (this.handleItemsAddedToCart) {
        EventBus.removeEventListener(Events.ITEMS_ADDED_TO_CART, this.handleItemsAddedToCart);
      }
      if (this.handleCartQuantityUpdated) {
        EventBus.removeEventListener(Events.CART_QUANTITY_UPDATED, this.handleCartQuantityUpdated);
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.product-details-dtf {
  $this: &;

  position: relative;

  &__jersey-numbers,
  &__letters {
    margin-bottom: 12px;
  }

  &__free-transfers-badge {
    margin-bottom: 16px;
  }

  &__file-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 36px;
    margin: -16px -18px 20px;
    background: $section-grey;
    border-bottom: 1px solid $gray-border;
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 700;
    padding: 12px;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;

    &--with-error {
      background: $red-error-background-2;
    }

    &--full-width {
      @include only-mobile {
        margin: -16px -5px 20px;
      }
    }

    @include with-desktop {
      font-size: 20px;
      height: 48px;
      margin-top: -20px;
      padding-left: 20px;
    }
  }

  &__pro-color-upsell {
    margin-bottom: 16px;
    border-color: $crafterpay-gray-modern-300;
  }

  &__artwork {
    margin-bottom: 16px;
    padding-bottom: 0;

    &--with-border {
      border: 1px solid $gray-border;
      padding: 10px 15px 20px 15px;
      border-radius: 8px;
      box-shadow: 0 25px 30px 0 rgba($black, 0.05);
      background-color: $white;
    }

    &--with-border-error {
      border-color: $red-border;
    }
  }

  &__dimensions {
    &--near-quantity {
      border-bottom: none;
      margin-bottom: 4px;
    }
  }

  &__upload {
    border-top: 1px solid #e1e1e5;
    margin: 12px 0;
    padding-top: 10px;
  }

  &__header-icon {
    width: 20px;
    height: 20px;
    margin-right: 10px;
    color: $feedback-success-darker;

    &--with-error {
      background: $red-error-background-2;
      color: $red;
    }
  }

  &__header-text {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__pro-color {
    margin-top: 12px;

    @include with-tablet {
      margin-top: 24px;
    }
  }

  &--jersey-numbers {
    :deep(.artwork-quantity__row) {
      align-items: flex-start;
    }

    :deep(.artwork-quantity__value) {
      margin-right: 32px;

      @include with-tablet {
        margin-right: 40px;
      }
    }

    :deep(.artwork-quantity__total) {
      margin: 0;
    }

    :deep(.artwork-quantity__prices) {
      align-self: unset;
      margin: 0 32px 0 0;

      @include with-tablet {
        margin: 0 40px 0 0;
      }
    }

    :deep(.artwork-quantity__off) {
      margin-top: 40px;
    }
  }

  &__section {
    margin-top: 12px;

    @include with-tablet {
      margin-top: 24px;
    }
  }

  &__store-credit {
    margin-top: 10px;
  }

  &__cancel {
    font-size: 14px;
    font-weight: 700;
    color: $primary-light;
    background: none;
    border: none;
    display: flex;
    align-items: center;
    height: 15px;
    margin-bottom: 5px;
  }

  &__cancel-icon {
    width: 14px;
    height: 14px;
    margin-right: 3px;
  }

  &__or {
    position: relative;
    display: inline-block;
    padding: 0 10px;
    text-align: center;
    width: 100%;
    font-weight: 700;

    &::before,
    &::after {
      content: "";
      position: absolute;
      top: 50%;
      height: 1px;
      background-color: $gray-border;
      width: 45%;

      @include with-desktop {
        width: 47%;
      }
    }

    &::before {
      right: 0;
    }

    &::after {
      left: 0;
    }
  }
}
</style>

<style lang="scss">
@import "frontend/init/jiffy/mixin.scss";

.product-details-dtf__dates-container {
  padding: 20px 0 0;
  margin-bottom: 25px;
  padding: 0;

  .product-details-quantity-form-row {
    &__shipping-progress-bar-wrapper {
      padding: 0 20px 20px;
      border-bottom: 1px solid $gray-border;
    }
  }
}
</style>
